import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Video, Phone, MapPin, CheckCircle2, Clock, Lock, Briefcase, CalendarDays } from "lucide-react";
import { isPortalAuthed } from "@/lib/portalAuth";

function useQuery() { return new URLSearchParams(useLocation().search); }
function fmtDate(d) { if (!d) return ""; const dt = new Date(d); return dt.toISOString().slice(0, 10); }
function fmtDayLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const wd = dt.toLocaleDateString("fr-FR", { weekday: "long" });
  const dm = dt.toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
  return { weekday: wd.charAt(0).toUpperCase() + wd.slice(1), dayMonth: dm };
}

const ICON = { visio: Video, phone: Phone, physical: MapPin };

export default function Booking() {
  const navigate = useNavigate();
  const q = useQuery();
  const isClientMode = q.get("client") === "1";
  const requestedType = q.get("type");

  const [types, setTypes] = useState([]);
  const [tab, setTab] = useState("");
  const [days, setDays] = useState([]);                // [{date, weekday, slots:[]}]
  const [loadingDays, setLoadingDays] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [pickedDate, setPickedDate] = useState("");
  const [pickedSlot, setPickedSlot] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: q.get("email") || "", phone: "", company: "", location: "", message: "" });
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load appointment types from backend
  useEffect(() => {
    api.get("/appointment-types").then((r) => {
      const all = r.data || [];
      const visible = isClientMode ? all : all.filter((t) => !t.requires_payment);
      setTypes(visible);
      const initial = (requestedType && visible.find((v) => v.code === requestedType)) || visible[0];
      if (initial) setTab(initial.code);
    }).catch(() => toast.error("Erreur chargement types de RDV"));
  }, [isClientMode, requestedType]);

  // Prefill from portal if connected
  useEffect(() => {
    if (!isPortalAuthed()) return;
    api.get("/portal/me").then((r) => {
      setForm((f) => ({
        ...f,
        first_name: r.data.first_name || (r.data.full_name || "").split(" ")[0] || f.first_name,
        last_name: r.data.last_name || (r.data.full_name || "").split(" ").slice(1).join(" ") || f.last_name,
        email: r.data.email || f.email,
        phone: r.data.phone || f.phone,
      }));
    }).catch(() => {});
    api.get("/portal/companies").then((r) => {
      setCompanies(r.data || []);
      if ((r.data || []).length === 1) {
        setCompanyId(r.data[0].id);
        setForm((f) => ({ ...f, company: r.data[0].name || f.company }));
      }
    }).catch(() => {});
  }, []);

  // Reset selection when type changes & reload days
  useEffect(() => {
    if (!tab) return;
    setPickedDate(""); setPickedSlot(""); setShowAll(false);
    setLoadingDays(true);
    api.get(`/availability/days`, { params: { type: tab, days: 28 } })
      .then((r) => setDays(r.data.days || []))
      .catch(() => setDays([]))
      .finally(() => setLoadingDays(false));
  }, [tab]);

  const activeType = useMemo(() => types.find((t) => t.code === tab), [types, tab]);
  const visibleDays = showAll ? days : days.slice(0, 5);

  const pickSlot = (date, time) => { setPickedDate(date); setPickedSlot(time); };

  const onPickCompany = (cid) => {
    setCompanyId(cid);
    const c = companies.find((x) => x.id === cid);
    if (c) setForm((f) => ({ ...f, company: c.name || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
    if (!fullName || !form.email) return toast.error("Prénom, nom et email requis");
    if (!pickedDate || !pickedSlot) return toast.error("Choisissez un créneau dans la liste ci-dessus");
    if (companies.length > 0 && !companyId) return toast.error("Sélectionnez l'entreprise concernée");
    setSubmitting(true);
    try {
      const payload = {
        type: tab, name: fullName, email: form.email, phone: form.phone, company: form.company,
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        company_id: companyId || null,
        preferred_date: pickedDate, preferred_time: pickedSlot,
        location: form.location || null, message: form.message,
      };
      const r = await api.post("/appointments", payload);
      if (activeType?.requires_payment) {
        const co = await api.post("/checkout/session", { appointment_id: r.data.id, origin_url: window.location.origin });
        window.location.href = co.data.url;
      } else {
        toast.success("Demande envoyée ! Vous recevrez la confirmation par email après validation.");
        navigate(isPortalAuthed() ? "/portail" : "/");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la réservation");
    } finally { setSubmitting(false); }
  };

  if (types.length === 0) {
    return (
      <>
        <Header />
        <main className="pt-36 sm:pt-40 pb-20 bg-[#FAFAFA] min-h-screen text-center">
          <div className="text-sm text-[#64748B]">Chargement des types de rendez-vous…</div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="pt-36 sm:pt-40 pb-20 bg-[#FAFAFA] min-h-screen" data-testid="booking-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">
              {isClientMode ? "Espace Entreprise · Réservation" : "Réservation"}
            </div>
            <h1 className="display mt-3 text-3xl sm:text-5xl text-[#0A1128]">
              {isClientMode ? "Réservez votre consultation" : "Prendre rendez-vous"}
            </h1>
            <p className="mt-3 text-[#475569]">
              {isClientMode ? "Choisissez le format adapté à votre besoin." : "RDV découverte gratuit, sans engagement."}
            </p>
            <div className="mt-4 max-w-2xl mx-auto bg-[#FFDCA8]/40 border border-[#FFDCA8] rounded-xl px-4 py-2.5 text-xs text-[#92400E]" data-testid="booking-approval-notice">
              ⓘ Toute demande de RDV est <b>soumise à approbation</b>. Vous recevrez un <b>email de confirmation</b> dès que BESSON SOLUTIONS aura validé votre créneau.
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mt-8">
            <TabsList className="grid w-full max-w-3xl mx-auto bg-white border border-black/8 p-1.5 rounded-full h-auto" style={{ gridTemplateColumns: `repeat(${types.length}, minmax(0, 1fr))` }}>
              {types.map((t) => {
                const Icon = ICON[t.delivery] || Video;
                return (
                  <TabsTrigger key={t.code} value={t.code} data-testid={`tab-${t.code}`} className="rounded-full text-xs sm:text-sm py-2.5 data-[state=active]:bg-[#0A1128] data-[state=active]:text-white">
                    <Icon size={14} className="mr-1.5" />{t.label}{t.price_eur > 0 ? ` · ${t.price_eur}€` : ""}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {types.map((t) => (
              <TabsContent key={t.code} value={t.code} className="mt-6">
                {t.description && (
                  <div className="max-w-3xl mx-auto bg-white border border-black/8 rounded-2xl p-4 text-sm text-[#475569] mb-5">
                    <div className="display font-extrabold text-[#0A1128] text-sm mb-1">{t.label}</div>
                    {t.description}
                    <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-[#64748B]">
                      <span><Clock size={11} className="inline mr-0.5" />{t.duration_minutes} min</span>
                      {t.price_eur > 0 ? <span className="text-[#0A1128] font-bold">{t.price_eur} € TTC</span> : <span className="text-green-600 font-bold">GRATUIT</span>}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Available days + slots */}
                  <div className="lg:col-span-5 bg-white rounded-3xl border border-black/8 p-5" data-testid="booking-days-panel">
                    <Label className="mb-3 block flex items-center gap-1.5"><CalendarDays size={14} className="text-[#06B6D4]" />1. Choisissez un créneau disponible</Label>
                    {loadingDays && <div className="text-xs text-[#64748B] py-6 text-center">Chargement des disponibilités…</div>}
                    {!loadingDays && days.length === 0 && (
                      <div className="text-xs text-[#92400E] bg-[#FFDCA8]/40 border border-[#FFDCA8] rounded-xl p-4">
                        Aucun créneau disponible dans les 28 prochains jours. Merci de revenir plus tard ou de nous contacter par email.
                      </div>
                    )}
                    {!loadingDays && days.length > 0 && (
                      <div className="space-y-3 max-h-[460px] overflow-y-auto pr-1" data-testid="booking-days-list">
                        {visibleDays.map((day) => {
                          const lbl = fmtDayLabel(day.date);
                          return (
                            <div key={day.date} className="rounded-xl border border-black/8 bg-[#FAFAFA]/60 p-3" data-testid={`booking-day-${day.date}`}>
                              <div className="flex items-baseline justify-between mb-2">
                                <div>
                                  <div className="text-[10px] font-bold tracking-widest uppercase text-[#06B6D4]">{lbl.weekday}</div>
                                  <div className="text-sm font-extrabold text-[#0A1128]">{lbl.dayMonth}</div>
                                </div>
                                <div className="text-[10px] text-[#64748B]">{day.slots.length} créneau{day.slots.length > 1 ? "x" : ""}</div>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {day.slots.map((s) => {
                                  const isPicked = pickedDate === day.date && pickedSlot === s;
                                  return (
                                    <button
                                      type="button"
                                      key={`${day.date}-${s}`}
                                      onClick={() => pickSlot(day.date, s)}
                                      data-testid={`slot-${day.date}-${s}`}
                                      className={`px-2.5 py-1 rounded-md text-xs font-bold tabular-nums border transition-colors ${isPicked ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-[#0A1128] border-black/10 hover:border-[#06B6D4] hover:text-[#06B6D4]"}`}
                                    >
                                      {s}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        {!showAll && days.length > 5 && (
                          <button type="button" onClick={() => setShowAll(true)} data-testid="booking-show-all" className="w-full text-xs font-bold text-[#06B6D4] hover:underline py-2">
                            Voir les {days.length - 5} jour{days.length - 5 > 1 ? "s" : ""} suivant{days.length - 5 > 1 ? "s" : ""} →
                          </button>
                        )}
                      </div>
                    )}
                    {pickedDate && pickedSlot && (
                      <div className="mt-3 px-3 py-2 rounded-xl bg-[#06B6D4]/10 border border-[#06B6D4]/30 text-xs font-bold text-[#0A1128]" data-testid="booking-selected">
                        Créneau choisi : <span className="text-[#06B6D4]">{fmtDayLabel(pickedDate).weekday} {fmtDayLabel(pickedDate).dayMonth} à {pickedSlot}</span>
                      </div>
                    )}
                  </div>

                  <div className="lg:col-span-7 bg-white rounded-3xl border border-black/8 p-6">
                    <Label className="mb-3 block">2. Vos coordonnées</Label>

                    {companies.length > 0 && (
                      <div className="mb-4 bg-[#06B6D4]/8 border border-[#06B6D4]/20 rounded-xl p-3" data-testid="booking-company-picker">
                        <Label className="flex items-center gap-1.5 text-[#0A1128] mb-2 text-xs"><Briefcase size={13} className="text-[#06B6D4]" />Pour quelle entreprise réservez-vous ?</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {companies.map((c) => (
                            <button key={c.id} type="button" onClick={() => onPickCompany(c.id)} data-testid={`booking-company-${c.id}`} className={`text-left px-3 py-2 rounded-lg border transition-colors text-sm ${companyId === c.id ? "bg-[#06B6D4] text-[#0A1128] border-[#06B6D4] font-bold" : "bg-white border-black/10 hover:border-[#06B6D4]"}`}>
                              <div className="font-bold truncate">{c.name}</div>
                              <div className="text-[10px] opacity-80 truncate">{c.sector || c.siret || ""}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={`booking-form-${t.code}`}>
                      <div><Label>Prénom *</Label><Input data-testid="booking-first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                      <div><Label>Nom *</Label><Input data-testid="booking-last-name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                      <div><Label>Email *</Label><Input data-testid="booking-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                      <div><Label>Téléphone</Label><Input data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                      {companies.length === 0 && <div><Label>Entreprise</Label><Input data-testid="booking-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>}
                      {t.delivery === "physical" && <div className="sm:col-span-2"><Label>Ville / Lieu</Label><Input data-testid="booking-location" placeholder="Istres, Marseille…" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>}
                      <div className="sm:col-span-2"><Label>Décrivez votre besoin</Label><Textarea data-testid="booking-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
                      <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between pt-2 border-t border-black/8">
                        <div className="text-xs text-[#64748B]">
                          {t.requires_payment ? `→ Paiement sécurisé Stripe (${t.price_eur} €) après envoi.` : "→ Vous recevrez un email après validation."}
                        </div>
                        <Button type="submit" disabled={submitting} data-testid="booking-submit" className="bg-[#0A1128] hover:bg-[#14213d] text-white rounded-full px-7 py-6">
                          {submitting ? "Envoi…" : t.requires_payment ? `Payer & réserver — ${t.price_eur}€` : "Demander ce RDV"}
                        </Button>
                      </div>
                    </form>
                    <div className="mt-5 bg-[#FAFAFA] rounded-xl p-4 border border-black/5 text-xs text-[#475569] space-y-1.5">
                      <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#06B6D4] mt-0.5" /> Confirmation par email après validation</div>
                      <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#06B6D4] mt-0.5" /> Annulation jusqu'à 24h avant</div>
                      <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#06B6D4] mt-0.5" /> Confidentialité garantie</div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </main>
      <Footer />
    </>
  );
}
